<template><div>

	<HeaderNav />
	<Breadcrumb />

	<b-container class="inner-content">

		<!-- Loading spinner -->
		<b-spinner v-if="processing" class="loading-spinner" />

		<!-- Cart view -->
		<div v-if="cartHasItems" :class="{ 'disabled': processing }">
			<!-- Cart table -->
			<h2>Cart</h2>
			<CartItems :items="items" @remove-item="removeItem" @update-quantity="updateItemQuantity" />

			<!-- Coupon area -->
			<h2 class="mt-md-4">Coupon</h2>
			<b-container class="coupon-area">
				<b-form-input
					v-model="couponCode"
					:state="couponValid"
					class="coupon-area__input"
					type="text"
					placeholder="Coupon code"
					@input="handleCouponInput"
					aria-label="Coupon Code"
				/>
				<b-button variant="primary" class="coupon-area__button" @click="applyCoupon">Apply coupon</b-button>
				<div v-if="couponValid === false" class="text-danger">The code you entered is invalid or expired.</div>
				<div v-if="couponValid === true" class="text-success">Code applied! (See below)</div>
			</b-container>

			<!-- Cart totals card -->
			<h2 class="mt-4">Cart totals</h2>
			<CartTotals
				:total="cart.amount_local.formatted"
				:subtotal="cart.subtotal_local.formatted"
				:coupons="coupons"
				:currency="cart.currency"
				@remove-coupon="removeItem"
			/>
		</div>

		<!-- No items in cart view -->
		<template v-else>
			<b-row class="mt-5">
				<b-col class="text-center">
					<i class="fas fa-shopping-cart empty-cart"></i>
					<div class="mt-3">
						<h5>You haven't added anything to your cart.</h5>
						<h5>We're sure you'll find something in our store!</h5>
					</div>
					<a href="/products" class="btn btn-primary mt-3">Continue Shopping</a>
				</b-col>
			</b-row>
		</template>

	</b-container>

</div></template>

<script>

	import api from '@js/api';
	import service from '@js/service/cartService';
	import HeaderNav from '@component/Header.vue';
	import Breadcrumb from '@component/Breadcrumb.vue';

	import CartItems from './CartItems.vue';
	import CartTotals from './CartTotals.vue';

	export default {
		components: {
			HeaderNav,
			Breadcrumb,
			CartItems,
			CartTotals
		},

		data() {
			return {
				couponCode: null,
				/* 3 states, null, true, or false */
				/* null = no change, true = success text and input state, false = error text and input state */
				couponValid: null,
				processing: false
			}
		},

		computed: {
			cart() {
				return this.$store.getters['cart/getCart'] || {};
			},

			items() {
				return this.cart.order_offers.filter(item => item.type === 'product');
			},

			coupons() {
				return this.cart.order_offers.filter(item => item.type === 'discount');
			},

			cartHasItems() {
				return this.cart && this.cart.order_offers && this.cart.order_offers.some(item => item.type === 'product');
			}
		},

		async beforeMount() {
			await service.subscribe('getCart');
		},

		methods: {
			async updateItemQuantity(item) {
				let { id, quantity } = item;

				this.processing = true;
        try {
          await this.$store.dispatch('cart/updateItemQuantity', { id, quantity });
        } catch(error) {
          console.error('Failed to update quantity!');
        }
				this.processing = false;
			},

			async removeItem(item) {
				this.processing = true;
        try {
          await this.$store.dispatch('cart/removeItem', item.id);
        } catch(error) {
          console.error('Failed to remove item!');
        }
				this.processing = false;
			},

			handleCouponInput(event) {
				this.couponValid = null;
			},

			async applyCoupon() {
				if (!this.couponCode) {
					return this.couponValid = false;
				}

				try {
					this.processing = true;
					await this.$store.dispatch('cart/applyCoupon', this.couponCode);
					this.couponValid = true;
				} catch(error) {
					console.error('Coupon not found!');
					this.couponValid = false;
				} finally {
					this.processing = false;
				}
			}
		}
	}

</script>

<style lang="scss" scoped>

	.coupon-area {
		background-color: white;
		border: 1px solid #dee2e6;
		border-radius: 10px;
		padding: 0.75rem;
	}

	.coupon-area__input {
		max-width: 280px;
		display: inline-block;
	}

	.coupon-area__button {
		margin-left: 0.5rem;
	}

	.empty-cart {
		font-size: 80px;
		color: lightgray;
	}

	@media (max-width: 767px) {
		.coupon-area__input {
			max-width: 100%;
			display: inline-block;
		}

		.coupon-area__button {
			width: 100%;
			margin-left: 0;
			margin-top: 0.75rem;
		}
	}

</style>
